import { useCallback } from "react";
import { PayeeImportBatchItemData } from "@wingspanhq/wingspan-api-sdk";
import { batchesApi } from "@api";

import { pMap } from "../../../../utils/pMap";
import { getApiErrorMessage, isApiError } from "../../../../v3/utils/apiError";
import { CUSTOM_FIELD_KEY_PREFIX } from "./contractorImportSheet";

export type ContractorImportRow = Record<string, unknown>;

export type ContractorImportUploadResult = {
  submitted: number;
  rejected: Array<{ rowIndex: number; message: string }>;
};

const text = (value: unknown) => String(value ?? "").trim();

/**
 * v1 collapsed a spreadsheet row into a first/last name pair plus a separate
 * W-9 name; v3 `PayeeImportBatchItemData` carries a single `displayName`.
 */
const toDisplayName = (row: ContractorImportRow) =>
  [text(row.firstName), text(row.lastName)].filter(Boolean).join(" ");

const toCustomFields = (row: ContractorImportRow) =>
  Object.entries(row).reduce<Record<string, string>>((acc, [key, value]) => {
    if (!key.startsWith(CUSTOM_FIELD_KEY_PREFIX) || value === undefined) {
      return acc;
    }
    const fieldKey = key.slice(CUSTOM_FIELD_KEY_PREFIX.length);
    const fieldValue = typeof value === "boolean" ? String(value) : text(value);
    return fieldValue ? { ...acc, [fieldKey]: fieldValue } : acc;
  }, {});

export const useContractorImportUpload = (batchId: string) =>
  useCallback(
    async (
      rows: ContractorImportRow[]
    ): Promise<ContractorImportUploadResult> => {
      const rejected: ContractorImportUploadResult["rejected"] = [];

      const items = rows.flatMap((row, rowIndex) => {
        const email = text(row.email).toLowerCase();
        const externalId = text(row.contractorId);
        const payeeId = text(row.payeeId);

        // The backend matches an existing contractor on any of the three and
        // creates one when none match, so a row carrying none of them
        // identifies nobody and would fail server-side anyway.
        if (!email && !externalId && !payeeId) {
          rejected.push({
            rowIndex,
            message:
              "This row has no email, external ID or Wingspan user ID, so there is nothing to match or create a contractor from."
          });
          return [];
        }

        // A missing name is not rejected here: only the backend knows whether
        // the row matches an existing contractor (where the sheet may legally
        // omit the name) or creates a new one (where it may not). Guessing
        // would reject valid update rows.
        const displayName = toDisplayName(row);
        const customFields = toCustomFields(row);
        const doingBusinessAs = text(row.businessName);
        const phone = text(row.phoneNumber);

        const data: PayeeImportBatchItemData = {
          ...(payeeId ? { payeeId } : {}),
          ...(externalId ? { externalId } : {}),
          ...(email ? { email } : {}),
          ...(displayName ? { displayName } : {}),
          ...(doingBusinessAs ? { doingBusinessAs } : {}),
          ...(phone ? { phone } : {}),
          ...(Object.keys(customFields).length > 0 ? { customFields } : {})
        };

        return [{ rowIndex, data }];
      });

      const { errors } = await pMap(
        items,
        (item: { rowIndex: number; data: PayeeImportBatchItemData }) =>
          // A replay of the same key returns the original item rather than
          // creating a second one, so a retry after a client-side timeout
          // cannot duplicate the row. The identity is the row's own
          // identifiers — a row index would restart on the next upload into
          // the same batch and dedup unrelated rows onto each other.
          batchesApi.addBatchItem({
            batchId,
            batchItemCreate: { data: item.data },
            idempotencyKey: `${batchId}:${item.data.payeeId ?? ""}:${
              item.data.externalId ?? ""
            }:${item.data.email ?? ""}`
          }),
        {
          concurrency: 25,
          retryConfig: {
            retries: 3,
            delay: 100,
            backoff: "EXPONENTIAL",
            // pMap's own predicate only understands axios errors, and the v3
            // SDK throws fetch-based `ApiError`s — without this every rejected
            // row would be re-POSTed three more times.
            retryIf: (error: unknown) =>
              !isApiError(error) || error.status >= 500
          }
        }
      );

      errors.forEach(({ item, error }) => {
        rejected.push({
          rowIndex: (item as { rowIndex: number }).rowIndex,
          message:
            getApiErrorMessage(error) ??
            (error as Error)?.message ??
            "Failed to import this row."
        });
      });

      return { submitted: items.length - errors.length, rejected };
    },
    [batchId]
  );
